import React, { useState, useEffect, useRef } from "react";
import { Close, Edit } from "@mui/icons-material";

const EditMessageModal = ({ isOpen, onClose, message, onSave, isSaving }) => {
  const [editedContent, setEditedContent] = useState("");
  const textareaRef = useRef(null);

  // ✅ All hooks must come BEFORE any early returns
  useEffect(() => {
    if (isOpen && message) {
      setEditedContent(message.content || "");
      // Focus textarea after modal opens
      setTimeout(() => {
        textareaRef.current?.focus();
        // Move cursor to end
        const length = message.content?.length || 0;
        textareaRef.current?.setSelectionRange(length, length);
      }, 100);
    }
  }, [isOpen, message]);

  // Auto-resize textarea
  useEffect(() => {
    if (textareaRef.current) {
      textareaRef.current.style.height = "auto";
      textareaRef.current.style.height = `${textareaRef.current.scrollHeight}px`;
    }
  }, [editedContent]);

  // ✅ Early return AFTER all hooks
  if (!isOpen || !message) return null;

  const handleBackdropClick = (e) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  const handleSave = () => {
    const trimmedContent = editedContent.trim();
    if (!trimmedContent || trimmedContent === message.content) {
      onClose();
      return;
    }
    onSave(message._id, trimmedContent);
  };

  const handleKeyDown = (e) => {
    // Save on Enter (without Shift)
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleSave();
    }
    // Close on Escape
    if (e.key === "Escape") {
      onClose();
    }
  };

  const isChanged = editedContent.trim() !== message.content;
  const canSave = editedContent.trim() && isChanged && !isSaving;

  return (
    <div
      className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50"
      onClick={handleBackdropClick}
    >
      <div className="bg-white rounded-xl shadow-2xl w-[500px] max-w-[90vw] mx-4 overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-gray-200 bg-gradient-to-r from-blue-50 to-blue-100">
          <div className="flex items-center gap-2">
            <Edit className="w-5 h-5 text-blue-600" />
            <h3 className="text-lg font-semibold text-gray-800">Edit Message</h3>
          </div>
          <button
            onClick={onClose}
            disabled={isSaving}
            className="text-gray-400 hover:text-gray-600 transition-colors disabled:opacity-50"
          >
            <Close className="w-6 h-6" />
          </button>
        </div>

        {/* Original Message Preview */}
        <div className="p-4 bg-gray-50 border-b border-gray-200">
          <div className="text-xs text-gray-500 mb-2 font-medium">
            Original message:
          </div>
          <div className="text-sm text-gray-600 italic bg-white p-3 rounded-lg border border-gray-200">
            {message.content}
          </div>
        </div>

        {/* Edit Area */}
        <div className="p-4">
          <textarea
            ref={textareaRef}
            value={editedContent}
            onChange={(e) => setEditedContent(e.target.value)}
            onKeyDown={handleKeyDown}
            disabled={isSaving}
            placeholder="Type your message..."
            className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-none disabled:bg-gray-100 disabled:cursor-not-allowed"
            style={{
              minHeight: "80px",
              maxHeight: "300px",
            }}
          />
          <div className="flex items-center justify-between mt-2">
            <div className="text-xs text-gray-500">
              Press Enter to save • Shift+Enter for new line • Esc to cancel
            </div>
            <div className="text-xs text-gray-400">
              {editedContent.length}/5000
            </div>
          </div>
        </div>

        {/* Time Limit Warning */}
        <div className="px-4 pb-2">
          <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3 text-xs text-yellow-700">
            ⚠️ You can only edit messages within 30 minutes of sending
          </div>
        </div>

        {/* Action Buttons */}
        <div className="flex gap-3 p-4 border-t border-gray-200 bg-gray-50">
          <button
            onClick={onClose}
            disabled={isSaving}
            className="flex-1 py-3 px-4 bg-white hover:bg-gray-100 text-gray-700 rounded-lg transition-colors border border-gray-300 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            Cancel
          </button>
          <button
            onClick={handleSave}
            disabled={!canSave}
            className="flex-1 py-3 px-4 bg-blue-600 hover:bg-blue-700 text-white rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed font-medium"
          >
            {isSaving ? (
              <span className="flex items-center justify-center gap-2">
                <span className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></span>
                Saving...
              </span>
            ) : (
              "Save Changes"
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

export default EditMessageModal;